import React, { useState, useEffect, useRef, useMemo } from 'react';
import { AnimatePresence, motion, MotionConfig } from 'framer-motion';
import {
    Box,
    Button,
    CircularProgress,
    Container,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    Skeleton,
    Typography,
    Link,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import IosShareIcon from '@mui/icons-material/IosShare';
import CloseIcon from '@mui/icons-material/Close';
import html2canvas from 'html2canvas';
import {
    getLiveRankings,
    type LiveRankingItem,
    type LiveRankingsCategory,
} from '../api/liveRankings';
import { useLiveRankingsAccessGate } from '../hooks/useLiveRankingsAccessGate';
import {
    useLiveRankingsCategoryStore,
    type LiveRankingCategoryFilter,
} from '../stores/liveRankingsCategoryStore';
import {
    ChartRow,
    RankNumber,
    ItemImage,
    MetricText,
    getS3Url,
    RankingLoadingSkeleton,
} from './rankings/RankingPageLayout';
import {
    LiveRankingCard,
    LIVE_ASSET_MAX_RANKINGS,
    LIVE_ASSET_WIDTH_PX,
    LIVE_ASSET_HEIGHT_PX,
    LIVE_ASSET_HEADER_TO_PILL_GAP_PX,
    LIVE_ASSET_PILL_TO_CONTENT_GAP_PX,
    liveRankingsCategoryCaption,
    liveRankingsCategoryDisplayLabel,
} from './LiveRankingsAssetRender';
import { MAIN_BG_COLOR } from '../theme';
import ContactDialog from '../components/ContactDialog';
import LiveRankingsGate from '../components/LiveRankingsGate';
import analytics, { AnalyticsEvent } from '../services/analytics';
import {
    formatLiveRankingsChipLabel,
    formatLiveTransmissionAirSentence,
} from '../utils/liveRankingsTimestamp';

export const REFETCH_INTERVAL = 1 * 60 * 1000;
export const LIVE_ROW_BG = 'rgba(33, 35, 45, 0.8)'; // #21232D at 70%

const CATEGORY_FILTER_TO_API: Record<LiveRankingCategoryFilter, LiveRankingsCategory | undefined> =
    {
        all: undefined,
        digital: 'digital',
        tv: 'tv',
        radio: 'radio',
    };

/** Stable Select width (short labels on xs; "Categoría: …" on sm+). */
const CATEGORY_SELECT_WIDTH_XS = 110;
const CATEGORY_SELECT_WIDTH_SM = 160;

/** Shared surface: category dropdown + Compartir (same shape, fill, border, shadow). */
const LIVE_HEADER_CHIP_HEIGHT_PX = 36;
const liveHeaderChipTypography = {
    color: 'white',
    fontFamily: '"DM Sans", sans-serif',
    fontSize: '0.82rem',
    fontWeight: 600,
} as const;
const liveHeaderChipShape = {
    height: `${LIVE_HEADER_CHIP_HEIGHT_PX}px`,
    borderRadius: '20px',
} as const;
const liveHeaderChipFill = {
    backgroundColor: 'rgba(255,255,255,0.14)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.12)',
    transition: 'background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
} as const;
/** Category + Compartir: same fill, border, radius, shadow (Select hides MUI notch so this actually shows). */
const liveHeaderChipBorderedSurface = {
    ...liveHeaderChipShape,
    ...liveHeaderChipFill,
    border: '1px solid rgba(255,255,255,0.5)',
} as const;
const liveHeaderChipHover = {
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderColor: 'rgba(255,255,255,0.75)',
} as const;
const liveHeaderChipFocus = {
    backgroundColor: 'rgba(255,255,255,0.22)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.28), 0 0 0 2px rgba(255,255,255,0.35)',
    borderColor: 'rgba(255,255,255,0.9)',
} as const;
const liveHeaderChipDisabled = {
    color: 'rgba(255,255,255,0.45)',
    borderColor: 'rgba(255,255,255,0.28)',
    backgroundColor: 'rgba(255,255,255,0.06)',
} as const;

/** Compact share/download chip buttons (tighter icon–label gap than MUI default). */
const liveShareChipButtonSx = {
    minWidth: 0,
    px: { xs: 0.625, sm: 1 },
    py: 0,
    height: `${LIVE_HEADER_CHIP_HEIGHT_PX}px`,
    borderRadius: 0,
    textTransform: 'none' as const,
    ...liveHeaderChipTypography,
    fontSize: { xs: '0.72rem', sm: '0.8rem' },
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '& .MuiButton-startIcon': {
        marginRight: '4px',
        marginLeft: '-2px',
    },
    '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.12)',
        boxShadow: 'none',
    },
    '&.Mui-focusVisible': {
        boxShadow: 'none',
    },
    '&.Mui-disabled': {
        color: liveHeaderChipDisabled.color,
    },
};

export const WeekBadge = styled(Typography)(() => ({
    color: 'white',
    fontSize: '0.9rem',
    fontWeight: 500,
    fontFamily: '"DM Sans", sans-serif',
    border: '2px solid rgba(255, 255, 255, 1)',
    padding: '4px 12px',
    borderRadius: '20px',
    display: 'inline-block',
    '@media (min-width: 600px)': {
        fontSize: '1rem',
        fontWeight: 500,
        padding: '6px 16px',
    },
}));

// Styled override of ChartRow for the dark background
export const LiveChartRow = styled(ChartRow)(() => ({
    backgroundColor: LIVE_ROW_BG,
    cursor: 'pointer',
    '& .MuiTypography-root': {
        color: 'white !important',
    },
    border: '1px solid rgba(255, 255, 255, 0.1)',
}));

export const PageContainer = styled(Box)(() => ({
    minHeight: '100vh',
    position: 'relative',
    backgroundColor: MAIN_BG_COLOR,
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url("/images/vertical-background.svg")',
        opacity: 0.5,
        backgroundSize: 'cover',
        backgroundPosition: 'bottom center',
        backgroundRepeat: 'no-repeat',
        zIndex: 0,
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(180deg,
            rgba(0, 0, 0, 0.1) 0%,
            rgba(0, 0, 0, 0.1) 60%,
            rgba(0, 0, 0, 0.25) 100%)`,
        zIndex: 0,
    },
}));

/** Matches `theme.ts` MuiContainer.maxWidthMd (800px — not MUI default 900px). */
export const LIVE_RANKINGS_PAGE_CONTENT_MAX_WIDTH_PX = 800;

export const LIVE_RANKINGS_PAGE_MAX_WIDTH = 'md' as const;

export const liveRankingsPageOuterSx = {
    pt: { xs: '20px', sm: '32px' },
    pb: '64px',
    position: 'relative',
    zIndex: 1,
} as const;

export const liveRankingsPageContainerSx = {
    px: { xs: 2, sm: 4 },
    maxWidth: `${LIVE_RANKINGS_PAGE_CONTENT_MAX_WIDTH_PX}px !important`,
} as const;

export const HeaderTitle = styled(Typography)(() => ({
    color: 'white',
    fontSize: '1.75rem',
    lineHeight: 1.2,
    fontWeight: 800,
    fontFamily: '"DM Serif Display", serif',
    '@media (min-width: 600px)': {
        fontSize: '2.25rem',
        lineHeight: 1.2,
    },
    '@media (min-width: 900px)': {
        fontSize: '2.75rem',
    },
}));

/** Smaller brand line above the main page title (/live and /ranking). */
export const HeaderBrandTitle = styled(Typography)(() => ({
    color: 'rgba(255, 255, 255, 0.92)',
    fontSize: '1.65rem',
    lineHeight: 1.25,
    fontWeight: 700,
    fontFamily: '"DM Serif Display", serif',
    '@media (min-width: 600px)': {
        fontSize: '2.1rem',
    },
    '@media (min-width: 900px)': {
        fontSize: '2.5rem',
    },
}));

export function isEffectivelyWhole(v: number): boolean {
    return Number.isInteger(v) || Math.abs(v - Math.round(v)) < 1e-6;
}

export function formatKOrM(value: number, divisor: number, suffix: string): string {
    const v = value / divisor;
    if (isEffectivelyWhole(v)) return `${Math.round(v).toLocaleString('es-CL')}${suffix}`;
    return `${v.toFixed(1).replace('.', ',')}${suffix}`;
}

export function formatLiveMetric(n: number): string {
    if (n >= 1_000_000) return formatKOrM(n, 1_000_000, 'M');
    if (n >= 1_000) return formatKOrM(n, 1_000, 'K');
    if (isEffectivelyWhole(n)) return Math.round(n).toLocaleString('es-CL');
    return n.toFixed(1).replace('.', ',');
}

export interface LiveRowProps {
    item: LiveRankingItem;
    staticSize?: boolean;
    /** End instant for on-air duration (rankings snapshot `updatedAt`). */
    referenceMs: number | null;
}

export const LiveRow: React.FC<LiveRowProps> = ({ item, staticSize = false, referenceMs }) => {
    const thumbnailUrl = item.channelThumbnail ? getS3Url(item.channelThumbnail) : undefined;
    const href = item.videoId ? `https://www.youtube.com/watch?v=${item.videoId}` : undefined;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{
                layout: { type: 'spring', stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
                y: { duration: 0.2 },
            }}
        >
            <Link
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'none', display: 'block' }}
            >
                <LiveChartRow
                    style={{
                        minHeight: staticSize ? '88px' : '104px',
                        height: 'auto',
                        gap: '12px',
                        paddingLeft: '20px',
                        paddingRight: '20px',
                        paddingTop: staticSize ? '8px' : '10px',
                        paddingBottom: staticSize ? '8px' : '10px',
                        borderRadius: '24px',
                    }}
                >
                    <RankNumber
                        sx={{
                            color: 'white !important',
                            minWidth: staticSize ? '32px' : { xs: '24px', sm: '32px' },
                            fontSize: staticSize ? '1.70rem' : undefined,
                            lineHeight: staticSize ? 1.2 : undefined,
                            alignSelf: 'center',
                        }}
                    >
                        {item.rank}.
                    </RankNumber>
                    <ItemImage
                        src={thumbnailUrl}
                        alt={item.channelName}
                        fallbackLetter={item.channelName.charAt(0)}
                        sizePx={staticSize ? 64 : 72}
                    />
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            pl: '6px',
                            overflow: 'hidden',
                            alignSelf: 'center',
                        }}
                    >
                        <MetricText
                            sx={{
                                ...(staticSize ? { fontSize: '1.85rem', lineHeight: 1.2 } : {}),
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                            }}
                        >
                            {item.channelName}
                        </MetricText>
                        {item.videoTitle ? (
                            <Typography
                                sx={{
                                    fontSize: staticSize
                                        ? '0.85rem'
                                        : { xs: '0.75rem', sm: '0.85rem' },
                                    color: 'rgba(255, 255, 255, 0.45) !important',
                                    fontFamily: '"DM Sans", sans-serif',
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    lineHeight: 1.2,
                                    maxWidth: '100%',
                                    mt: 0.25,
                                }}
                            >
                                {item.videoTitle}
                            </Typography>
                        ) : null}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                mt: item.videoTitle ? 0.35 : 0.25,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    px: '6px',
                                    py: '1px',
                                    borderRadius: '4px',
                                    fontSize: staticSize
                                        ? '0.65rem'
                                        : { xs: '0.58rem', sm: '0.63rem' },
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontWeight: 700,
                                    letterSpacing: '0.02em',
                                    lineHeight: 1.4,
                                    flexShrink: 0,
                                    background: 'rgba(255, 50, 50, 0.15)',
                                    color: '#FF5252',
                                    border: '1px solid rgba(255, 50, 50, 0.35)',
                                }}
                            >
                                {item.streamType === 'premiere' ? 'ESTRENO' : 'EN DIRECTO'}
                            </Box>
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: staticSize
                                        ? '0.78rem'
                                        : { xs: '0.68rem', sm: '0.75rem' },
                                    color: 'rgba(255, 255, 255, 0.38) !important',
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontWeight: 500,
                                    lineHeight: 1.45,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {formatLiveTransmissionAirSentence(
                                    item.startedAt ?? null,
                                    referenceMs
                                )}
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: 0.1,
                            flexShrink: 0,
                            alignSelf: 'center',
                        }}
                    >
                        <MetricText
                            sx={{
                                minWidth: staticSize ? '80px' : { xs: '50px', sm: '80px' },
                                textAlign: 'right',
                                color: 'white !important',
                                fontSize: staticSize ? '1.5rem' : undefined,
                                lineHeight: staticSize ? 1.2 : undefined,
                            }}
                        >
                            {formatLiveMetric(item.viewers)}
                        </MetricText>
                        <Typography
                            sx={{
                                fontSize: staticSize ? '0.85rem' : { xs: '0.75rem', sm: '0.85rem' },
                                color: 'rgba(255, 255, 255, 0.45) !important',
                                fontFamily: '"DM Sans", sans-serif',
                                textAlign: 'right',
                                minWidth: staticSize ? '80px' : { xs: '50px', sm: '80px' },
                            }}
                        >
                            {'Peak: '}
                            {formatLiveMetric(item.peakViewers)}
                        </Typography>
                    </Box>
                </LiveChartRow>
            </Link>
        </motion.div>
    );
};

const POPUP_DELAY_MS = 10_000;

export interface LiveRankingsPopupProps {
    totalCount: number;
    totalViewers: number;
}

export const LiveRankingsPopup: React.FC<LiveRankingsPopupProps> = ({
    totalCount,
    totalViewers,
}) => {
    const [visible, setVisible] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setVisible(true), POPUP_DELAY_MS);
        return () => clearTimeout(timer);
    }, []);

    const handleDismiss = () => {
        setVisible(false);
    };

    const handleOpenContact = () => {
        analytics.track(AnalyticsEvent.CLICK_LIVE_RANKINGS_CONTACT, {
            source: 'live_rankings_popup',
        });
        setContactOpen(true);
    };

    const handleContactClose = () => {
        setContactOpen(false);
        setVisible(false);
    };

    if (!visible && !contactOpen) return null;

    const showLeadCard = visible && !contactOpen;

    return (
        <>
            {showLeadCard && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: '16px',
                        right: '16px',
                        left: { xs: '16px', sm: 'auto' },
                        width: { xs: 'auto', sm: '300px' },
                        zIndex: 1400,
                        backgroundColor: LIVE_ROW_BG,
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                    }}
                >
                    {/* Header row */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            pt: 1.5,
                            pb: 0.5,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: '"DM Serif Display", serif',
                                fontWeight: 800,
                                fontSize: '1rem',
                                color: 'white',
                                lineHeight: 1,
                            }}
                        >
                            Podscope
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handleDismiss}
                            aria-label="Cerrar"
                            sx={{
                                color: 'rgba(255,255,255,0.4)',
                                p: '4px',
                                '&:hover': { color: 'rgba(255,255,255,0.8)' },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    </Box>

                    {/* Message */}
                    <Box>
                        <Typography
                            sx={{
                                px: 2,
                                pt: 0.5,
                                fontSize: '0.875rem',
                                color: 'rgba(255,255,255,0.85)',
                                fontFamily: '"DM Sans", sans-serif',
                                lineHeight: 1.5,
                            }}
                        >
                            {'Hay '}
                            <strong style={{ color: 'white' }}>{totalCount}</strong>
                            {' livestreams en vivo ahora con '}
                            <strong style={{ color: 'white' }}>
                                {formatLiveMetric(totalViewers)}
                            </strong>
                            {' espectadores en total.'}
                        </Typography>
                        <Typography
                            sx={{
                                px: 2,
                                pt: 0.5,
                                pb: 1.5,
                                fontSize: '0.875rem',
                                color: 'rgba(255,255,255,0.85)',
                                fontFamily: '"DM Sans", sans-serif',
                                lineHeight: 1.5,
                            }}
                        >
                            {
                                'En Podscope medimos programas, redes sociales y presencia de marcas en contenido digital.'
                            }
                        </Typography>
                    </Box>

                    {/* CTA */}
                    <Box sx={{ px: 2, pb: 2 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleOpenContact}
                            sx={{
                                background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                                color: '#FFFFFF',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontFamily: '"DM Sans", sans-serif',
                                fontSize: '0.875rem',
                                borderRadius: '8px',
                                py: 1,
                                border: 'none',
                                boxShadow: 'none',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            Quiero tener acceso
                        </Button>
                    </Box>
                </Box>
            )}

            <ContactDialog
                open={contactOpen}
                onClose={handleContactClose}
                initialMessage="Hola, vi el ranking en vivo y quiero saber más sobre Podscope"
            />
        </>
    );
};

const LiveRankings: React.FC = () => {
    const theme = useTheme();
    const isMobileCategoryLabel = useMediaQuery(theme.breakpoints.down('sm'));
    const categoryFilter = useLiveRankingsCategoryStore(s => s.categoryFilter);
    const setCategoryFilter = useLiveRankingsCategoryStore(s => s.setCategoryFilter);

    const [persistHydrated, setPersistHydrated] = useState(() =>
        useLiveRankingsCategoryStore.persist.hasHydrated()
    );
    useEffect(() => {
        if (useLiveRankingsCategoryStore.persist.hasHydrated()) {
            setPersistHydrated(true);
            return undefined;
        }
        return useLiveRankingsCategoryStore.persist.onFinishHydration(() => {
            setPersistHydrated(true);
        });
    }, []);

    const categoryParam = CATEGORY_FILTER_TO_API[categoryFilter];

    const {
        accessStatusPending,
        gatedNoAccess,
        canFetchContent: canFetchRankings,
        accessUrlFlash,
    } = useLiveRankingsAccessGate();

    const rankingsQueryEnabled = persistHydrated && canFetchRankings;

    const { data, isLoading, isFetching } = useQuery({
        queryKey: ['live-rankings', categoryParam ?? 'all'],
        queryFn: () => getLiveRankings(categoryParam),
        enabled: rankingsQueryEnabled,
        refetchInterval: REFETCH_INTERVAL,
        refetchIntervalInBackground: true,
        staleTime: REFETCH_INTERVAL,
    });

    // Unscoped fetch: totalCount / totalViewers for all live streams (popup only; list stays category-filtered)
    const { data: globalTotalsData } = useQuery({
        queryKey: ['live-rankings', 'global-totals'],
        queryFn: () => getLiveRankings(),
        enabled: rankingsQueryEnabled,
        refetchInterval: REFETCH_INTERVAL,
        refetchIntervalInBackground: true,
        staleTime: REFETCH_INTERVAL,
    });

    const listLoading = !persistHydrated || accessStatusPending || isLoading;

    // Pre-generated blob — ready before the user taps share so navigator.share()
    // can be called synchronously (required by iOS Safari's user-gesture policy).
    const [shareBlob, setShareBlob] = useState<Blob | null>(null);
    const [isGenerating, setIsGenerating] = useState(true);
    const generatingRef = useRef(false);

    const rankings = data?.rankings ?? [];

    const referenceMs = useMemo(() => {
        const raw = data?.updatedAt;
        if (raw === undefined || raw === null) return null;
        const ms = new Date(raw as string).getTime();
        return Number.isFinite(ms) ? ms : null;
    }, [data?.updatedAt]);

    useEffect(() => {
        setShareBlob(null);
        generatingRef.current = false;
    }, [categoryParam]);

    const captureShareBlob = async (element: HTMLElement): Promise<Blob | null> => {
        // Safari caches non-CORS image responses and re-serves them for CORS requests,
        // stripping the ACAO header → browser blocks them (the "— status" bug).
        // Fix: only cross-origin S3 images get fetched fresh and converted to same-origin
        // blob URLs. Same-origin images (e.g. /ranking-bg.png) are left alone so
        // html2canvas loads them normally without any risk of taint.
        const S3_ORIGIN = 'https://podscope.s3.amazonaws.com';

        // Fetch image → data URL (inline base64). Data URLs are pure strings: no network
        // request inside html2canvas, no blob-URL context issues, no CORS re-check.
        const fetchToDataUrl = async (src: string): Promise<string | null> => {
            try {
                const res = await fetch(src, { mode: 'cors', cache: 'no-store' });
                if (!res.ok) return null;
                const blob = await res.blob();
                return new Promise<string | null>(resolve => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result as string);
                    reader.onerror = () => resolve(null);
                    reader.readAsDataURL(blob);
                });
            } catch {
                return null;
            }
        };

        const imgEls = Array.from(element.querySelectorAll('img')) as HTMLImageElement[];
        // Map from original S3 src → pre-painted canvas (pixel data already drawn).
        // html2canvas reads <canvas> pixel data directly — no network, no CORS check.
        const srcToCanvas = new Map<string, HTMLCanvasElement>();

        await Promise.all(
            imgEls.map(async img => {
                if (!img.src || !img.src.startsWith(S3_ORIGIN)) return;
                let dataUrl = await fetchToDataUrl(img.src);
                if (!dataUrl) {
                    const altSrc = img.src.endsWith('.jpg')
                        ? img.src.replace(/\.jpg$/, '.png')
                        : img.src.endsWith('.png')
                          ? img.src.replace(/\.png$/, '.jpg')
                          : null;
                    if (altSrc) dataUrl = await fetchToDataUrl(altSrc);
                }
                if (!dataUrl) return;

                await new Promise<void>(resolve => {
                    const paintImg = new Image();
                    paintImg.onload = () => {
                        const c = document.createElement('canvas');
                        c.width = paintImg.naturalWidth || 52;
                        c.height = paintImg.naturalHeight || 52;
                        const ctx = c.getContext('2d');
                        if (ctx) ctx.drawImage(paintImg, 0, 0);
                        srcToCanvas.set(img.src, c);
                        resolve();
                    };
                    paintImg.onerror = () => resolve();
                    paintImg.src = dataUrl;
                });
            })
        );

        const canvas = await html2canvas(element, {
            scale: 2,
            useCORS: false,
            allowTaint: false,
            width: 1080,
            height: 1350,
            backgroundColor: '#1a1a1a',
            onclone: (_doc: Document, cloned: HTMLElement) => {
                cloned.style.position = 'relative';
                cloned.style.top = '0';
                cloned.style.left = '0';
                cloned.style.transform = 'none';
                cloned.style.opacity = '1';
                cloned.style.width = `${LIVE_ASSET_WIDTH_PX}px`;
                cloned.style.height = `${LIVE_ASSET_HEIGHT_PX}px`;
                cloned.style.minHeight = `${LIVE_ASSET_HEIGHT_PX}px`;
                cloned.style.maxHeight = `${LIVE_ASSET_HEIGHT_PX}px`;
                cloned.style.boxSizing = 'border-box';
                cloned.style.display = 'grid';
                if (cloned.parentElement) {
                    cloned.parentElement.style.cssText =
                        'position:fixed;top:0;left:0;width:1080px;height:1350px;overflow:visible;transform:none;opacity:1;z-index:0;pointer-events:none;';
                }
                cloned.querySelectorAll('[data-live-asset-period-pill]').forEach(el => {
                    (el as HTMLElement).style.paddingTop = `${LIVE_ASSET_HEADER_TO_PILL_GAP_PX}px`;
                    (el as HTMLElement).style.boxSizing = 'border-box';
                });
                cloned.querySelectorAll('[data-live-asset-rankings]').forEach(el => {
                    (el as HTMLElement).style.paddingTop = `${LIVE_ASSET_PILL_TO_CONTENT_GAP_PX}px`;
                    (el as HTMLElement).style.boxSizing = 'border-box';
                });
                // framer-motion with reducedMotion leaves rows at initial opacity:0 — force visible.
                Array.from(cloned.querySelectorAll<HTMLElement>('*')).forEach(el => {
                    if (el.style.opacity === '0') el.style.opacity = '1';
                    if (el.style.visibility === 'hidden') el.style.visibility = 'visible';
                });

                // Replace each S3 <img> with a pre-painted <canvas> clone.
                // html2canvas renders <canvas> elements by reading their pixel data directly —
                // no src attribute, no network request, no CORS re-check of any kind.
                Array.from(cloned.querySelectorAll('img')).forEach(img => {
                    const preCanvas = srcToCanvas.get(img.src);
                    if (!preCanvas || !img.parentElement) return;
                    const c = document.createElement('canvas');
                    c.width = preCanvas.width;
                    c.height = preCanvas.height;
                    const ctx = c.getContext('2d');
                    if (ctx) ctx.drawImage(preCanvas, 0, 0);
                    c.style.cssText =
                        'width:100%;height:100%;display:block;border-radius:12px;position:absolute;top:0;left:0;';
                    img.parentElement.replaceChild(c, img);
                });
            },
        });

        return new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
    };

    useEffect(() => {
        // No rankings yet — stop spinner so button doesn't stay stuck
        if (!rankings.length) {
            if (!listLoading) setIsGenerating(false);
            return;
        }
        if (generatingRef.current) return;
        const timer = setTimeout(async () => {
            const element = document.getElementById('share-asset');
            if (!element) {
                setIsGenerating(false);
                return;
            }
            generatingRef.current = true;
            setIsGenerating(true);
            try {
                const blob = await captureShareBlob(element);
                setShareBlob(blob);
            } catch (err) {
                console.error('Pre-generate share image failed:', err);
            } finally {
                setIsGenerating(false);
                generatingRef.current = false;
            }
        }, 2000);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rankings.length, listLoading, categoryParam]);

    const sharePngFilename = () => `podscope-live-${new Date().toISOString().slice(0, 10)}.png`;

    const doDownloadPng = (blob: Blob) => {
        const filename = sharePngFilename();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    const doShare = (blob: Blob) => {
        const filename = sharePngFilename();
        const file = new File([blob], filename, { type: 'image/png' });
        if (navigator.canShare?.({ files: [file] })) {
            navigator
                .share({
                    title: `Top ${LIVE_ASSET_MAX_RANKINGS} livestreams en vivo · podscope.co/live`,
                    files: [file],
                })
                .catch(err => {
                    if (err?.name !== 'AbortError') console.error('Share failed:', err);
                });
        } else {
            doDownloadPng(blob);
        }
    };

    // On mobile, navigator.share must be called synchronously within the user gesture,
    // so we pre-generate the blob. On desktop we can generate on demand as a fallback.
    const handleShare = () => {
        if (gatedNoAccess) return;
        if (shareBlob) {
            doShare(shareBlob);
            return;
        }
        // Pre-generation failed — fall back to on-demand (desktop/download path only)
        const element = document.getElementById('share-asset');
        if (!element) return;
        setIsGenerating(true);
        captureShareBlob(element)
            .then(blob => {
                if (blob) {
                    setShareBlob(blob);
                    doShare(blob);
                }
            })
            .catch(err => console.error('On-demand share image failed:', err))
            .finally(() => setIsGenerating(false));
    };

    const handleDownloadPng = () => {
        if (gatedNoAccess) return;
        const run = (blob: Blob) => doDownloadPng(blob);
        if (shareBlob) {
            run(shareBlob);
            return;
        }
        const element = document.getElementById('share-asset');
        if (!element) return;
        setIsGenerating(true);
        captureShareBlob(element)
            .then(blob => {
                if (blob) {
                    setShareBlob(blob);
                    run(blob);
                }
            })
            .catch(err => console.error('On-demand download image failed:', err))
            .finally(() => setIsGenerating(false));
    };

    const updatedAtLabel = formatLiveRankingsChipLabel(data?.updatedAt);
    const cardUpdatedAtLabel = updatedAtLabel;
    const gatedTimePillLabel = 'Última actualización disponible al ingresar';

    const shareActionsDisabled = gatedNoAccess || isGenerating || !rankings.length;

    if (accessStatusPending) {
        return (
            <>
                <Helmet>
                    <title>En Vivo ahora | Podscope</title>
                    <meta
                        name="description"
                        content="Top 10 transmisiones en vivo con más espectadores en este momento."
                    />
                </Helmet>
                <PageContainer>
                    <Box
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            width: '100%',
                            minHeight: '60vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress sx={{ color: 'white' }} />
                    </Box>
                </PageContainer>
            </>
        );
    }

    return (
        <>
            <Helmet>
                <title>En Vivo ahora | Podscope</title>
                <meta
                    name="description"
                    content="Top 10 transmisiones en vivo con más espectadores en este momento."
                />
            </Helmet>

            <PageContainer>
                <Box sx={liveRankingsPageOuterSx}>
                    <Container
                        maxWidth={LIVE_RANKINGS_PAGE_MAX_WIDTH}
                        sx={liveRankingsPageContainerSx}
                    >
                        {/* Header */}
                        <Box
                            sx={{
                                mb: gatedNoAccess ? '16px' : '32px',
                                textAlign: 'center',
                                position: 'relative',
                                width: '100%',
                            }}
                        >
                            {/*
                              Mobile: row1 = category | share, row2 = EN VIVO centered.
                              sm+: one row — category (start) | EN VIVO (center) | share (end).
                            */}
                            <Box
                                sx={{
                                    display: 'grid',
                                    width: '100%',
                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                    columnGap: 1,
                                    rowGap: { xs: 1.5, sm: 0 },
                                    gridTemplateColumns: {
                                        xs: '1fr auto',
                                        sm: 'minmax(0,1fr) auto minmax(0,1fr)',
                                    },
                                    gridTemplateRows: { xs: 'auto auto', sm: 'auto' },
                                    mb: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        gridColumn: { xs: '1', sm: '1' },
                                        gridRow: { xs: '1', sm: '1' },
                                        justifySelf: 'start',
                                        alignSelf: { xs: 'flex-start', sm: 'center' },
                                        width: {
                                            xs: `min(${CATEGORY_SELECT_WIDTH_XS}px, calc(100% - 48px))`,
                                            sm: `${CATEGORY_SELECT_WIDTH_SM}px`,
                                        },
                                        minWidth: {
                                            xs: `min(${CATEGORY_SELECT_WIDTH_XS}px, calc(100% - 48px))`,
                                            sm: `${CATEGORY_SELECT_WIDTH_SM}px`,
                                        },
                                        maxWidth: {
                                            xs: 'calc(100% - 48px)',
                                            sm: 'none',
                                        },
                                        zIndex: 1,
                                        display: 'inline-flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        boxSizing: 'border-box',
                                        ...liveHeaderChipBorderedSurface,
                                        ...(gatedNoAccess
                                            ? {}
                                            : {
                                                  '&:hover': {
                                                      ...liveHeaderChipHover,
                                                      boxShadow: liveHeaderChipFill.boxShadow,
                                                  },
                                                  '&:focus-within': {
                                                      ...liveHeaderChipFocus,
                                                  },
                                              }),
                                        ...(gatedNoAccess && {
                                            opacity: 0.55,
                                            pointerEvents: 'none',
                                        }),
                                    }}
                                >
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        sx={{
                                            m: 0,
                                            mt: 0,
                                            mb: 0,
                                            height: '100%',
                                            minHeight: 0,
                                            display: 'flex',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Select<LiveRankingCategoryFilter>
                                            value={categoryFilter}
                                            disabled={gatedNoAccess}
                                            onChange={e =>
                                                setCategoryFilter(
                                                    e.target.value as LiveRankingCategoryFilter
                                                )
                                            }
                                            displayEmpty
                                            renderValue={value => {
                                                const cat = value === 'all' ? undefined : value;
                                                return isMobileCategoryLabel
                                                    ? liveRankingsCategoryDisplayLabel(cat)
                                                    : liveRankingsCategoryCaption(cat);
                                            }}
                                            inputProps={{ 'aria-label': 'Filtrar por categoría' }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        bgcolor: 'rgba(33, 35, 45, 0.98)',
                                                        border: '1px solid rgba(255,255,255,0.2)',
                                                        borderRadius: '12px',
                                                        mt: 0.5,
                                                        '& .MuiMenuItem-root': {
                                                            color: 'rgba(255,255,255,0.9)',
                                                            fontFamily: '"DM Sans", sans-serif',
                                                            fontSize: '0.85rem',
                                                            fontWeight: 500,
                                                        },
                                                        '& .MuiMenuItem-root.Mui-selected': {
                                                            bgcolor: 'rgba(255,255,255,0.12)',
                                                        },
                                                        '& .MuiMenuItem-root:hover': {
                                                            bgcolor: 'rgba(255,255,255,0.08)',
                                                        },
                                                    },
                                                },
                                            }}
                                            sx={{
                                                width: '100%',
                                                minWidth: 0,
                                                ...liveHeaderChipTypography,
                                                borderRadius: liveHeaderChipShape.borderRadius,
                                                '& .MuiOutlinedInput-root': {
                                                    width: '100%',
                                                    height: '100%',
                                                    minHeight: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    overflow: 'hidden',
                                                    backgroundColor: 'transparent',
                                                    boxShadow: 'none',
                                                    borderRadius: liveHeaderChipShape.borderRadius,
                                                    '&:hover': {
                                                        backgroundColor: 'transparent',
                                                    },
                                                    '&.Mui-focused': {
                                                        backgroundColor: 'transparent',
                                                        boxShadow: 'none',
                                                    },
                                                },
                                                '& .MuiSelect-select': {
                                                    backgroundColor: 'transparent !important',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    pr: '32px !important',
                                                    py: 0,
                                                    minHeight: 0,
                                                    height: '100%',
                                                    lineHeight: 1.2,
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    border: 'none !important',
                                                },
                                                '& .MuiSvgIcon-root': { color: 'white' },
                                                '&.Mui-disabled': {
                                                    '& .MuiSelect-select': {
                                                        color: liveHeaderChipDisabled.color,
                                                        WebkitTextFillColor:
                                                            liveHeaderChipDisabled.color,
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        color: liveHeaderChipDisabled.color,
                                                    },
                                                },
                                            }}
                                        >
                                            <MenuItem value="all">Todas</MenuItem>
                                            <MenuItem value="digital">Digital</MenuItem>
                                            <MenuItem value="tv">TV</MenuItem>
                                            <MenuItem value="radio">Radio</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Box
                                    sx={{
                                        gridColumn: { xs: '2', sm: '3' },
                                        gridRow: { xs: '1', sm: '1' },
                                        justifySelf: 'end',
                                        alignSelf: { xs: 'flex-start', sm: 'center' },
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        gap: 0.5,
                                        maxWidth: {
                                            xs: 'min(100%, calc(100vw - 120px))',
                                            sm: 'none',
                                        },
                                        ...(shareActionsDisabled && {
                                            opacity: 0.55,
                                        }),
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'stretch',
                                            ...liveHeaderChipBorderedSurface,
                                            '&:hover': {
                                                ...liveHeaderChipHover,
                                                boxShadow: liveHeaderChipFill.boxShadow,
                                            },
                                            '&:focus-within': {
                                                ...liveHeaderChipFocus,
                                            },
                                        }}
                                    >
                                        <Button
                                            type="button"
                                            variant="text"
                                            disableElevation
                                            onClick={handleShare}
                                            disabled={shareActionsDisabled}
                                            aria-label="Compartir ranking"
                                            startIcon={
                                                isGenerating ? (
                                                    <CircularProgress
                                                        size={16}
                                                        thickness={5}
                                                        sx={{
                                                            color: 'inherit',
                                                            display: 'block',
                                                        }}
                                                    />
                                                ) : (
                                                    <IosShareIcon sx={{ fontSize: '1rem' }} />
                                                )
                                            }
                                            sx={liveShareChipButtonSx}
                                        >
                                            Compartir
                                        </Button>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'stretch',
                                            ...liveHeaderChipBorderedSurface,
                                            '&:hover': {
                                                ...liveHeaderChipHover,
                                                boxShadow: liveHeaderChipFill.boxShadow,
                                            },
                                            '&:focus-within': {
                                                ...liveHeaderChipFocus,
                                            },
                                        }}
                                    >
                                        <Button
                                            type="button"
                                            variant="text"
                                            disableElevation
                                            onClick={handleDownloadPng}
                                            disabled={shareActionsDisabled}
                                            aria-label="Descargar imagen PNG del ranking"
                                            startIcon={
                                                isGenerating ? (
                                                    <CircularProgress
                                                        size={16}
                                                        thickness={5}
                                                        sx={{
                                                            color: 'inherit',
                                                            display: 'block',
                                                        }}
                                                    />
                                                ) : (
                                                    <FileDownloadOutlinedIcon
                                                        sx={{ fontSize: '1rem' }}
                                                    />
                                                )
                                            }
                                            sx={liveShareChipButtonSx}
                                        >
                                            Descargar
                                        </Button>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        gridColumn: { xs: '1 / -1', sm: '2' },
                                        gridRow: { xs: '2', sm: '1' },
                                        justifySelf: 'center',
                                        alignSelf: 'center',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        backgroundColor: 'rgba(229, 57, 53, 0.15)',
                                        border: '2px solid rgba(229, 57, 53, 0.4)',
                                        borderRadius: '20px',
                                        px: 1.5,
                                        py: 0.5,
                                        height: `${LIVE_HEADER_CHIP_HEIGHT_PX}px`,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: '9px',
                                            height: '9px',
                                            borderRadius: '50%',
                                            backgroundColor: '#ef5350',
                                            flexShrink: 0,
                                            '@keyframes livePulse': {
                                                '0%': { opacity: 1, transform: 'scale(1)' },
                                                '50%': { opacity: 0.4, transform: 'scale(0.7)' },
                                                '100%': { opacity: 1, transform: 'scale(1)' },
                                            },
                                            animation: 'livePulse 1.6s ease-in-out infinite',
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            color: '#ef5350',
                                            fontSize: '0.82rem',
                                            fontWeight: 700,
                                            letterSpacing: '0.1em',
                                            fontFamily: '"DM Sans", sans-serif',
                                        }}
                                    >
                                        EN VIVO
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ mt: 4, mb: gatedNoAccess ? 1.5 : 3, width: '100%' }}>
                                <HeaderBrandTitle
                                    component="p"
                                    sx={{ m: 0, mb: 0.75, width: '100%' }}
                                >
                                    Podscope Top 10
                                </HeaderBrandTitle>
                                <HeaderTitle variant="h1" sx={{ m: 0, width: '100%' }}>
                                    Livestreams en YouTube
                                </HeaderTitle>
                            </Box>

                            {gatedNoAccess ? (
                                <WeekBadge
                                    sx={{
                                        maxWidth: { xs: 'min(100%, 340px)', sm: 520 },
                                        whiteSpace: 'normal',
                                        textAlign: 'center',
                                        lineHeight: 1.35,
                                        py: 1,
                                        px: 1.5,
                                        fontSize: { xs: '0.78rem', sm: '0.9rem' },
                                    }}
                                >
                                    {gatedTimePillLabel}
                                </WeekBadge>
                            ) : listLoading ? (
                                <Skeleton
                                    variant="rounded"
                                    animation="wave"
                                    sx={{
                                        display: 'inline-block',
                                        verticalAlign: 'middle',
                                        height: { xs: '30px', sm: '34px' },
                                        width: { xs: 'min(260px, 72vw)', sm: '280px' },
                                        borderRadius: '20px',
                                        bgcolor: 'rgba(255,255,255,0.12)',
                                    }}
                                />
                            ) : isFetching && !isLoading ? (
                                <Box
                                    sx={{
                                        position: 'relative',
                                        display: 'inline-flex',
                                        padding: '2px',
                                        borderRadius: '22px',
                                        overflow: 'hidden',
                                        maxWidth: '100%',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            width: '200%',
                                            height: '400%',
                                            top: '50%',
                                            left: '50%',
                                            background:
                                                'linear-gradient(transparent, rgba(255,255,255,0.9), transparent)',
                                            '@keyframes spinBadgeBorder': {
                                                from: {
                                                    transform: 'translate(-50%, -50%) rotate(0deg)',
                                                },
                                                to: {
                                                    transform:
                                                        'translate(-50%, -50%) rotate(360deg)',
                                                },
                                            },
                                            animation: 'spinBadgeBorder 1.5s linear infinite',
                                        },
                                    }}
                                >
                                    <WeekBadge
                                        sx={{
                                            border: 'none',
                                            background: MAIN_BG_COLOR,
                                            position: 'relative',
                                            zIndex: 1,
                                            maxWidth: { xs: 'min(100%, 340px)', sm: 520 },
                                            whiteSpace: 'normal',
                                            textAlign: 'center',
                                            lineHeight: 1.35,
                                            py: 1,
                                            px: 1.5,
                                            fontSize: { xs: '0.78rem', sm: '0.9rem' },
                                        }}
                                    >
                                        {updatedAtLabel}
                                    </WeekBadge>
                                </Box>
                            ) : (
                                <WeekBadge
                                    sx={{
                                        maxWidth: { xs: 'min(100%, 340px)', sm: 520 },
                                        whiteSpace: 'normal',
                                        textAlign: 'center',
                                        lineHeight: 1.35,
                                        py: 1,
                                        px: 1.5,
                                        fontSize: { xs: '0.78rem', sm: '0.9rem' },
                                    }}
                                >
                                    {updatedAtLabel}
                                </WeekBadge>
                            )}
                        </Box>

                        {gatedNoAccess && (
                            <Box
                                sx={{
                                    position: 'relative',
                                    zIndex: 1,
                                    width: '100%',
                                    mt: 1.5,
                                    py: 5,
                                }}
                            >
                                <LiveRankingsGate accessError={accessUrlFlash} />
                            </Box>
                        )}

                        {/* Rankings list */}
                        <Box sx={{ mx: 'auto' }}>
                            {listLoading && !gatedNoAccess && (
                                <RankingLoadingSkeleton count={10} backgroundColor={LIVE_ROW_BG} />
                            )}

                            {!listLoading && !gatedNoAccess && rankings.length === 0 && (
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        py: 8,
                                        color: 'rgba(255,255,255,0.5)',
                                        fontFamily: '"DM Sans", sans-serif',
                                        fontSize: '1rem',
                                    }}
                                >
                                    No hay transmisiones en vivo en este momento
                                </Box>
                            )}

                            <AnimatePresence>
                                {!listLoading &&
                                    !gatedNoAccess &&
                                    rankings.map(item => (
                                        <LiveRow
                                            key={item.videoId ?? item.channelName}
                                            item={item}
                                            referenceMs={referenceMs}
                                        />
                                    ))}
                            </AnimatePresence>
                        </Box>

                        {/* Footer note */}
                        <Typography
                            sx={{
                                mt: 4,
                                textAlign: 'center',
                                color: 'rgba(255, 255, 255, 0.4)',
                                fontSize: '0.8rem',
                                fontStyle: 'italic',
                                fontFamily: '"DM Sans", sans-serif',
                            }}
                        >
                            Datos actualizados cada 5 minutos
                        </Typography>
                    </Container>
                </Box>
            </PageContainer>

            {!gatedNoAccess && (
                <LiveRankingsPopup
                    totalCount={globalTotalsData?.totalCount ?? 0}
                    totalViewers={globalTotalsData?.totalViewers ?? 0}
                />
            )}

            {/* Hidden card for html2canvas.
                Must stay in the viewport (top:0 left:0) so iOS Safari loads the images —
                iOS skips fetching images for elements far outside the viewport.
                opacity:0 + zIndex:-1 keep it invisible and behind all page content.
                onclone restores opacity:1 so html2canvas renders it correctly. */}
            {!gatedNoAccess && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: LIVE_ASSET_WIDTH_PX,
                        height: LIVE_ASSET_HEIGHT_PX,
                        opacity: 0,
                        pointerEvents: 'none',
                        zIndex: -1,
                        overflow: 'hidden',
                    }}
                >
                    <MotionConfig reducedMotion="always">
                        <LiveRankingCard
                            id="share-asset"
                            rankings={rankings.slice(0, LIVE_ASSET_MAX_RANKINGS)}
                            updatedAtLabel={cardUpdatedAtLabel}
                            category={categoryParam}
                            isLoading={listLoading}
                            referenceMs={referenceMs}
                        />
                    </MotionConfig>
                </Box>
            )}
        </>
    );
};

export default LiveRankings;
